import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { GuestRatingSummary } from '@apps/main/new-desktop/modules/customersRating';
import type {
  HotelIngredient,
  RecipeMainIngredient,
} from '@apps/main/modules/recipe/ingredients';
import type { DateWithItinerary } from '@apps/main/new-desktop/core/stores/trip/tripStore.model';

import {
  isAvailableFerryIngredientResponse,
  isAvailableFlightIngredientResponse,
} from '@libs/search-service-smart-search';
import { DateFormat, SemboDate } from '@libs/sembo-dates';
import type {
  CarrierStaticInfo,
  ContentApiImage,
  DestinationStaticInfo,
  GuestRatingBreakdown,
  HotelBookableOptionWithPriceBreakdown,
  HotelIngredientResponse,
  OverviewDestinationStaticInfo,
  OverviewStaticInfoResult,
  PortStaticInfo,
} from '@libs/trip-planner-interfaces';
import { IngredientType, TransportType } from '@libs/trip-planner-interfaces';

import type {
  PeekPriceLargeHotelCardData,
  PeekPriceMediumBookableTransportCardData,
  PeekPriceModalFerryPlusHotelItinerary,
  PeekPriceModalFlightPlusHotelItinerary,
  PeekPriceModalHotelOnlyItinerary,
  PeekPriceModalMultiIngredientItinerary,
  PeekPriceMultiIngredientCard,
  PeekPriceRoomDetails,
  PeekPriceSmallBookableTransportCardData,
  PeekPriceSmallHotelCardData,
  PeekPriceUnbookableTransportCardData,
} from '../models';
import { PeekPriceModalItineraryType, PeekPriceModalCardType } from '../models';

import {
  getAccommodationDatesText,
  getAccommodationSubtitleText,
  getMultiIngredientAccommodationCardLoadingData,
  getAccommodationNightsText,
  getAccommodationImageUrls,
} from './accommodation';
import {
  getMultiIngredientBookableTransportCardLoadingData,
  getPortsText,
  getStopsText,
  getTimeText,
} from './bookableTransport';
import {
  getOwnTransportIcon,
  getOwnTransportDescriptionText,
} from './ownTransport';

const { t } = i18n.global;

// NOTE: HOTEL ONLY

interface PeekPriceModalHotelOnlyItineraryParams {
  displayDate: Date | undefined;
  paxes: string;
  hotelAvail: HotelIngredientResponse | undefined;
  hotelStaticInfo: OverviewDestinationStaticInfo | DestinationStaticInfo;
  getImageUrl: (file: ContentApiImage) => string;
  formatMoney: (money: number) => string;
  getGuestRating: (
    guestRatings: GuestRatingBreakdown | undefined
  ) => GuestRatingSummary | undefined;
  isSearched: boolean;
}

export function getPeekPriceModalHotelOnlyItinerary(
  params: PeekPriceModalHotelOnlyItineraryParams
): PeekPriceModalHotelOnlyItinerary {
  const imagesLimit = 3;
  const amount = params.hotelAvail?.suggested?.totalPrice.amount;
  const price = amount ? params.formatMoney(amount) : undefined;
  const isUnavailable = params.isSearched && !price;
  const rooms: PeekPriceRoomDetails[] | undefined =
    params?.hotelAvail?.suggested?.rooms.map(
      (room: HotelBookableOptionWithPriceBreakdown) => ({
        description: room.description,
        boardBasisCode: room.boardBasis,
      })
    );
  const numberOfNights = params.hotelAvail?.numberOfNights;
  const dates = numberOfNights
    ? getAccommodationDatesText(params.displayDate, numberOfNights)
    : '';
  const hotel: PeekPriceLargeHotelCardData = {
    dates,
    imageUrls: getAccommodationImageUrls(
      params.hotelStaticInfo.images,
      imagesLimit,
      params.getImageUrl
    ),
    nights: getAccommodationNightsText(numberOfNights),
    hotelName: params.hotelStaticInfo.name,
    hotelDestination: getAccommodationSubtitleText(params.hotelStaticInfo),
    hotelStars: params.hotelStaticInfo.semboSunRating,
    guestRating: params.getGuestRating(params.hotelStaticInfo.guestRatings),
    rooms,
  };

  const hotelOnlyItinerary: PeekPriceModalHotelOnlyItinerary = {
    type: PeekPriceModalItineraryType.HotelOnly,
    dates,
    price,
    paxes: params.paxes,
    hotel,
    isUnavailable,
  };

  return hotelOnlyItinerary;
}

// NOTE: FLIGHT PLUS HOTEL
// NOTE: FERRY PLUS HOTEL
// NOTE: BOOKABLE TRANSPORT PLUS HOTEL
interface GetUnavailableBookableTransportPlusHotelItineraryParams {
  type: 'flight' | 'ferry';
  hotelIngredient: HotelIngredient;
  hotelStaticInfo: OverviewDestinationStaticInfo | DestinationStaticInfo;
  paxes: string;
  imagesLimit: number;
  getImageUrl: (file: ContentApiImage) => string;
  getGuestRating: (
    guestRatings: GuestRatingBreakdown | undefined
  ) => GuestRatingSummary | undefined;
}

export function getUnavailableBookableTransportPlusHotelItinerary(
  params: GetUnavailableBookableTransportPlusHotelItineraryParams
):
  | PeekPriceModalFlightPlusHotelItinerary
  | PeekPriceModalFerryPlusHotelItinerary {
  const numberOfNights = params.hotelIngredient.numberOfNights;
  const notAvailHotel: PeekPriceLargeHotelCardData = {
    dates: undefined,
    imageUrls: getAccommodationImageUrls(
      params.hotelStaticInfo.images,
      params.imagesLimit,
      params.getImageUrl
    ),
    nights: getAccommodationNightsText(numberOfNights),
    hotelName: params.hotelStaticInfo.name,
    hotelDestination: getAccommodationSubtitleText(params.hotelStaticInfo),
    hotelStars: params.hotelStaticInfo.semboSunRating,
    guestRating: params.getGuestRating(params.hotelStaticInfo.guestRatings),
    rooms: undefined,
  };

  const notAvailDepartureTransport: PeekPriceMediumBookableTransportCardData = {
    date: undefined,
    direction: t('filter.flightFilters.departure'),
    stops: undefined,
    carrierLogoUrl: undefined,
    carrierLogoAltText:
      params.type === 'flight'
        ? t('a11y.flight_carrier_logo')
        : t('a11y.ferry_carrier_logo'),
    altLogoIcon: params.type === 'flight' ? 'airplane' : 'ferry',
    time: undefined,
    ports: undefined,
  };

  const notAvailArrivalTransport: PeekPriceMediumBookableTransportCardData = {
    date: undefined,
    direction: t('filter.flightFilters.arrival'),
    stops: undefined,
    carrierLogoUrl: undefined,
    carrierLogoAltText:
      params.type === 'flight'
        ? t('a11y.flight_carrier_logo')
        : t('a11y.ferry_carrier_logo'),
    altLogoIcon: params.type === 'flight' ? 'airplane' : 'ferry',
    time: undefined,
    ports: undefined,
  };

  const notAvailableRecipe: PeekPriceModalFlightPlusHotelItinerary = {
    type: PeekPriceModalItineraryType.FlightPlusHotel,
    dates: undefined,
    price: undefined,
    paxes: params.paxes,
    departureTransport: notAvailDepartureTransport,
    hotel: notAvailHotel,
    arrivalTransport: notAvailArrivalTransport,
    isUnavailable: true,
  };

  return notAvailableRecipe;
}

interface PeekPriceModalBookableTransportPlusHotelItineraryParams {
  peekPriceRecipe: RecipeMainIngredient[];
  selectedItinerary: DateWithItinerary | undefined;
  hotelStaticInfo: OverviewDestinationStaticInfo | DestinationStaticInfo;
  paxes: string;
  displayDate: Date | undefined;
  type:
    | PeekPriceModalItineraryType.FlightPlusHotel
    | PeekPriceModalItineraryType.FerryPlusHotel;
  getImageUrl: (file: ContentApiImage) => string;
  formatMoney: (money: number) => string;
  getGuestRating: (
    guestRatings: GuestRatingBreakdown | undefined
  ) => GuestRatingSummary | undefined;
  getPortStaticInfo: (
    code: string,
    kind: IngredientType
  ) => PortStaticInfo | undefined;
  getCarriersStaticInfo: (
    carriers: string[]
  ) => Record<string, CarrierStaticInfo>;
  isSearched: boolean;
}

export function getPeekPriceModalBookableTransportPlusHotelItinerary(
  params: PeekPriceModalBookableTransportPlusHotelItineraryParams
):
  | PeekPriceModalFlightPlusHotelItinerary
  | PeekPriceModalFerryPlusHotelItinerary {
  const imagesLimit = 3;
  const hotelIngredient = params.peekPriceRecipe[1] as HotelIngredient;
  const numberOfNights = hotelIngredient.numberOfNights;
  const isFlight = params.type === PeekPriceModalItineraryType.FlightPlusHotel;
  const ingredientType = isFlight
    ? IngredientType.Flight
    : IngredientType.Ferry;
  const notAvailableRecipe = getUnavailableBookableTransportPlusHotelItinerary({
    type: isFlight ? 'flight' : 'ferry',
    hotelIngredient,
    hotelStaticInfo: params.hotelStaticInfo,
    paxes: params.paxes,
    imagesLimit: 3,
    getImageUrl: params.getImageUrl,
    getGuestRating: params.getGuestRating,
  });

  const departureIngredient = isFlight
    ? params.selectedItinerary?.itinerary?.flightOnlyItinerary[0]
    : params.selectedItinerary?.itinerary?.ferryOnlyItinerary[0];
  const arrivalIngredient = isFlight
    ? params.selectedItinerary?.itinerary?.flightOnlyItinerary[1]
    : params.selectedItinerary?.itinerary?.ferryOnlyItinerary[1];

  const typeGuard = isFlight
    ? isAvailableFlightIngredientResponse
    : isAvailableFerryIngredientResponse;

  if (
    !departureIngredient ||
    !arrivalIngredient ||
    !typeGuard(departureIngredient) ||
    !typeGuard(arrivalIngredient)
  ) {
    return notAvailableRecipe;
  }

  const departureTime = getTimeText(
    departureIngredient.departureDate,
    departureIngredient.arrivalDate
  );
  const arrivalTime = getTimeText(
    arrivalIngredient.departureDate,
    arrivalIngredient.arrivalDate
  );
  const firstTransportDeparturePort = params.getPortStaticInfo(
    departureIngredient.departureIata,
    ingredientType
  );

  const firstTransportArrivalPort = params.getPortStaticInfo(
    departureIngredient.arrivalIata,
    ingredientType
  );

  const secondTransportDeparturePort = params.getPortStaticInfo(
    arrivalIngredient.departureIata,
    ingredientType
  );

  const secondTransportArrivalPort = params.getPortStaticInfo(
    arrivalIngredient.arrivalIata,
    ingredientType
  );

  if (
    !firstTransportDeparturePort ||
    !firstTransportArrivalPort ||
    !secondTransportDeparturePort ||
    !secondTransportArrivalPort
  ) {
    return notAvailableRecipe;
  }

  const departureCarrier =
    departureIngredient.platingCarrier ?? departureIngredient.carriers[0];
  const arrivalCarrier =
    arrivalIngredient.platingCarrier ?? arrivalIngredient.carriers[0];

  if (!departureCarrier || !arrivalCarrier) {
    return notAvailableRecipe;
  }

  const carrierStaticInfoDict = params.getCarriersStaticInfo([
    departureCarrier,
    arrivalCarrier,
  ]);

  const departureTransport: PeekPriceMediumBookableTransportCardData = {
    date: new SemboDate(departureIngredient.departureDate).format(
      DateFormat.DateMonth
    ),
    direction: t('filter.flightFilters.departure'),
    stops:
      'numberOfStops' in departureIngredient
        ? getStopsText(departureIngredient.numberOfStops)
        : t('planner.direct_flight_short'),
    carrierLogoUrl: carrierStaticInfoDict[departureCarrier]?.logo,
    carrierLogoAltText: t('a11y.flight_carrier_logo'),
    altLogoIcon: isFlight ? 'airplane' : 'ferry',
    time: departureTime,
    ports: getPortsText(firstTransportDeparturePort, firstTransportArrivalPort),
  };

  const arrivalTransport: PeekPriceMediumBookableTransportCardData = {
    date: new SemboDate(arrivalIngredient.departureDate).format(
      DateFormat.DateMonth
    ),
    direction: t('filter.flightFilters.arrival'),
    stops:
      'numberOfStops' in arrivalIngredient
        ? getStopsText(arrivalIngredient.numberOfStops)
        : t('planner.direct_flight_short'),
    carrierLogoUrl: carrierStaticInfoDict[arrivalCarrier]?.logo,
    carrierLogoAltText: t('a11y.flight_carrier_logo'),
    altLogoIcon: isFlight ? 'airplane' : 'ferry',
    time: arrivalTime,
    ports: getPortsText(
      secondTransportDeparturePort,
      secondTransportArrivalPort
    ),
  };

  const totalPrice = params.selectedItinerary?.itinerary?.totalPrice;
  const price = totalPrice ? params.formatMoney(totalPrice.amount) : undefined;

  const rooms: PeekPriceRoomDetails[] | undefined =
    params.selectedItinerary?.itinerary?.hotelOnlyItinerary[0]?.suggested?.rooms.map(
      (room: HotelBookableOptionWithPriceBreakdown) => ({
        description: room.description,
        boardBasisCode: room.boardBasis,
      })
    );
  const dates = numberOfNights
    ? getAccommodationDatesText(params.displayDate, numberOfNights)
    : '';
  const hotel: PeekPriceLargeHotelCardData = {
    dates,
    imageUrls: getAccommodationImageUrls(
      params.hotelStaticInfo.images,
      imagesLimit,
      params.getImageUrl
    ),
    nights: getAccommodationNightsText(numberOfNights),
    hotelName: params.hotelStaticInfo.name,
    hotelDestination: getAccommodationSubtitleText(params.hotelStaticInfo),
    hotelStars: params.hotelStaticInfo.semboSunRating,
    guestRating: params.getGuestRating(params.hotelStaticInfo.guestRatings),
    rooms,
  };

  const bookableTransportPlusHotelRecipe:
    | PeekPriceModalFlightPlusHotelItinerary
    | PeekPriceModalFerryPlusHotelItinerary = {
    type: params.type,
    dates,
    price,
    paxes: params.paxes,
    departureTransport,
    hotel,
    arrivalTransport,
    isUnavailable: params.isSearched && !price,
  };

  return bookableTransportPlusHotelRecipe;
}

// NOTE: MULTI INGREDIENT RECIPE

interface GetPeekPriceModalMultiIngredientItineraryParams {
  displayDate: Date | undefined;
  peekPriceRecipe: RecipeMainIngredient[];
  selectedItinerary: DateWithItinerary | undefined;
  hotelStaticInfo: OverviewDestinationStaticInfo | DestinationStaticInfo;
  itineraryStaticInfo: OverviewStaticInfoResult;
  paxes: string;
  getImageUrl: (file: ContentApiImage) => string;
  formatMoney: (money: number) => string;
  getPortStaticInfo: (
    code: string,
    kind: IngredientType
  ) => PortStaticInfo | undefined;
  getCarriersStaticInfo: (
    carriers: string[]
  ) => Record<string, CarrierStaticInfo>;
  isSearched: boolean;
}

export function getPeekPriceModalMultiIngredientItinerary(
  params: GetPeekPriceModalMultiIngredientItineraryParams
): PeekPriceModalMultiIngredientItinerary {
  const imagesLimit = 1;

  const cards = params.peekPriceRecipe
    .map((ingredient: RecipeMainIngredient, index: number) => {
      const availability =
        params.selectedItinerary?.itinerary?.itinerary[index];

      if (ingredient.ingredientType === IngredientType.Hotel) {
        const loadingCard = getMultiIngredientAccommodationCardLoadingData({
          date: availability?.date,
          numberOfNights: ingredient.numberOfNights ?? 0,
          hotelStaticInfo:
            ingredient.ingredientId === params.hotelStaticInfo.ingredientId
              ? params.hotelStaticInfo
              : undefined,
          getImageUrl: params.getImageUrl,
        });

        if (
          availability?.ingredientType !== IngredientType.Hotel ||
          !availability.suggested?.code ||
          !params.itineraryStaticInfo
        ) {
          return loadingCard;
        }

        const hotelStaticInfo =
          params.itineraryStaticInfo.destinationStaticInfo[
            `${availability.suggested.code}${ingredient.ingredientId}`
          ];

        if (!hotelStaticInfo) {
          return loadingCard;
        }

        const nights = getAccommodationNightsText(availability.numberOfNights);
        const rooms = availability.suggested.rooms.map(
          (room: HotelBookableOptionWithPriceBreakdown) => ({
            description: room.description,
            boardBasisCode: room.boardBasis,
          })
        );

        const dates = getAccommodationDatesText(
          availability.date,
          availability.numberOfNights
        );
        const data: PeekPriceSmallHotelCardData = {
          dates,
          imageUrl: getAccommodationImageUrls(
            hotelStaticInfo.images,
            imagesLimit,
            params.getImageUrl
          )[0],
          nights,
          hotelName: hotelStaticInfo.name,
          hotelDestination: getAccommodationSubtitleText(hotelStaticInfo),
          rooms,
        };

        return {
          type: PeekPriceModalCardType.Accommodation,
          data,
        };
      }

      if (ingredient.ingredientType === IngredientType.Flight) {
        const loadingCard = getMultiIngredientBookableTransportCardLoadingData(
          ingredient.ingredientType
        );

        if (
          !availability ||
          !isAvailableFlightIngredientResponse(availability)
        ) {
          return loadingCard;
        }

        const time = getTimeText(
          availability.departureDate,
          availability.arrivalDate
        );
        const departurePort = params.getPortStaticInfo(
          availability.departureIata,
          IngredientType.Flight
        );

        const arrivalPort = params.getPortStaticInfo(
          availability.arrivalIata,
          IngredientType.Flight
        );

        if (!departurePort || !arrivalPort) {
          return loadingCard;
        }

        const carrier = availability.platingCarrier ?? availability.carriers[0];

        if (!carrier) {
          return loadingCard;
        }

        const carrierStaticInfoDict = params.getCarriersStaticInfo([carrier]);

        const data: PeekPriceSmallBookableTransportCardData = {
          date: new SemboDate(availability.departureDate).format(
            DateFormat.DateMonth
          ),
          stops: getStopsText(availability.numberOfStops),
          carrierLogoUrl: carrierStaticInfoDict[carrier]?.logo,
          carrierLogoAltText: t('a11y.flight_carrier_logo'),
          altLogoIcon: 'airplane',
          time,
          ports: getPortsText(departurePort, arrivalPort),
        };
        return {
          type: PeekPriceModalCardType.BookableTransport,
          data,
        };
      }

      if (ingredient.ingredientType === IngredientType.Ferry) {
        const loadingCard = getMultiIngredientBookableTransportCardLoadingData(
          ingredient.ingredientType
        );

        if (
          !availability ||
          !isAvailableFerryIngredientResponse(availability)
        ) {
          return loadingCard;
        }

        const time = getTimeText(
          availability.departureDate,
          availability.arrivalDate
        );
        const departurePort = params.getPortStaticInfo(
          availability.departureIata,
          IngredientType.Ferry
        );
        const arrivalPort = params.getPortStaticInfo(
          availability.arrivalIata,
          IngredientType.Ferry
        );

        if (!departurePort || !arrivalPort) {
          return loadingCard;
        }

        const carrier = availability.platingCarrier ?? availability.carriers[0];

        if (!carrier) {
          return loadingCard;
        }

        const carrierStaticInfoDict = params.getCarriersStaticInfo([carrier]);

        const data: PeekPriceSmallBookableTransportCardData = {
          date: new SemboDate(availability.departureDate).format(
            DateFormat.DateMonth
          ),
          stops: t('planner.direct_flight_short'),
          carrierLogoUrl: carrierStaticInfoDict[carrier]?.logo,
          carrierLogoAltText: t('a11y.flight_carrier_logo'),
          altLogoIcon: 'airplane',
          time,
          ports: getPortsText(departurePort, arrivalPort),
        };
        return {
          type: PeekPriceModalCardType.BookableTransport,
          data,
        };
      }

      if (
        ingredient.ingredientType === IngredientType.OwnTransport &&
        ingredient.ownTransportType !== TransportType.None
      ) {
        if (!availability) {
          const data: PeekPriceUnbookableTransportCardData = {
            type: ingredient.ownTransportType,
            date: undefined,
            icon: getOwnTransportIcon(ingredient.ownTransportType),
            description: getOwnTransportDescriptionText(
              ingredient.ownTransportType
            ),
          };
          return {
            type: PeekPriceModalCardType.UnbookableTransport,
            data,
          };
        }

        const data: PeekPriceUnbookableTransportCardData = {
          type: ingredient.ownTransportType,
          date: new SemboDate(availability.date).format(DateFormat.DateMonth),
          icon: getOwnTransportIcon(ingredient.ownTransportType),
          description: getOwnTransportDescriptionText(
            ingredient.ownTransportType
          ),
        };
        return {
          type: PeekPriceModalCardType.UnbookableTransport,
          data,
        };
      }

      return undefined;
    })
    .filter((card) => !!card) as PeekPriceMultiIngredientCard[];

  const numberOfNights = params.peekPriceRecipe.reduce(
    (acc: number, ingredient: RecipeMainIngredient) => {
      if (
        ingredient.ingredientType === IngredientType.Hotel ||
        ingredient.ingredientType === IngredientType.OwnStay
      ) {
        return acc + (ingredient.numberOfNights ?? 0);
      }
      return acc;
    },
    0
  );
  const dates = numberOfNights
    ? getAccommodationDatesText(params.displayDate, numberOfNights)
    : '';
  const totalPrice = params.selectedItinerary?.itinerary?.totalPrice;
  const price = totalPrice ? params.formatMoney(totalPrice.amount) : undefined;
  const itinerary: PeekPriceModalMultiIngredientItinerary = {
    type: PeekPriceModalItineraryType.MultiIngredient,
    paxes: params.paxes,
    dates,
    price,
    cards,
    inspectedAccommodationName: params.hotelStaticInfo.name,
    isUnavailable: params.isSearched && !price,
  };
  return itinerary;
}
